import { ChangeEvent, useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/th";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { previewDemandAdjustment } from "../../services";
import {
  DemandAdjustmentPreview,
  DemandAdjustmentPreviewRequest,
} from "../../types/demand-factor.type";
import CustomerPicker, { CustomerOption } from "../SpecialOrder/CustomerPicker";
import CodeMultiSelect from "./CodeMultiSelect";
import { API_DATE_FORMAT } from "./constants";
import PreviewResult from "./PreviewResult";
import { DemandFactorLookups } from "./useDemandFactorLookups";

interface PreviewDialogProps {
  open: boolean;
  onClose: () => void;
  /** Head office and master pick the store's branch first: customer codes repeat across branches. */
  canSelectBranch: boolean;
  lookups: DemandFactorLookups;
}

const HALF_WIDTH = { xs: 12, md: 6 };

/** Answers the preview, or null when the API refused it (the interceptor shows why). */
const requestPreview = async (
  request: DemandAdjustmentPreviewRequest,
): Promise<DemandAdjustmentPreview | null> => {
  try {
    const {
      data: { data },
    } = await previewDemandAdjustment(request);
    return data;
  } catch {
    // The axios interceptor already showed the API's Thai message (e.g. the store is not in
    // the chosen branch); the inputs stay as they are so they can be corrected.
    return null;
  }
};

/** "ทดลองคำนวณ": which rows reach one store on one date, and the multiplier each product gets. */
export default function PreviewDialog({
  open,
  onClose,
  canSelectBranch,
  lookups,
}: PreviewDialogProps) {
  const [branchCode, setBranchCode] = useState<string>("");
  const [customer, setCustomer] = useState<CustomerOption | null>(null);
  const [date, setDate] = useState<Dayjs | null>(() => dayjs());
  const [productCodes, setProductCodes] = useState<string[]>([]);
  const [preview, setPreview] = useState<DemandAdjustmentPreview | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!open) return;
    setBranchCode("");
    setCustomer(null);
    setDate(dayjs());
    setProductCodes([]);
    setPreview(null);
  }, [open]);

  const selectedBranch = lookups.branches.find((branch) => branch.branch_code === branchCode);
  const isBranchMissing = canSelectBranch && !selectedBranch;
  const canPreview = Boolean(customer) && Boolean(date?.isValid()) && !isBranchMissing;

  // Any change to the inputs makes the answer on screen stale.
  const handleBranchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setBranchCode(event.target.value);
    setCustomer(null);
    setPreview(null);
  };

  const handleCustomerChange = (next: CustomerOption | null) => {
    setCustomer(next);
    setPreview(null);
  };

  const handleDateChange = (next: Dayjs | null) => {
    setDate(next);
    setPreview(null);
  };

  const handleProductChange = (codes: string[]) => {
    setProductCodes(codes);
    setPreview(null);
  };

  const handlePreview = async () => {
    if (!customer || !date?.isValid() || isBranchMissing) return;
    setLoading(true);
    const answer = await requestPreview({
      customer_code: customer.customer_code,
      date: date.format(API_DATE_FORMAT),
      branch_code: selectedBranch?.branch_code,
      product_codes: productCodes.length > 0 ? productCodes : undefined,
    });
    setPreview(answer);
    setLoading(false);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>ทดลองคำนวณ Demand Factor</DialogTitle>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
        <DialogContent dividers>
          <Stack gap={3}>
            <Grid container spacing={2}>
              {canSelectBranch && (
                <Grid size={HALF_WIDTH}>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    label="สาขาของร้าน"
                    value={branchCode}
                    onChange={handleBranchChange}
                  >
                    {lookups.branches.map((branch) => (
                      <MenuItem key={branch.uuid} value={branch.branch_code}>
                        {branch.branch_code} - {branch.branch_name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}
              <Grid size={HALF_WIDTH}>
                <CustomerPicker
                  value={customer}
                  onChange={handleCustomerChange}
                  branchUuid={selectedBranch?.uuid}
                  disabled={isBranchMissing}
                  helperText={isBranchMissing ? "เลือกสาขาก่อน" : undefined}
                />
              </Grid>
              <Grid size={HALF_WIDTH}>
                <DatePicker
                  label="วันที่"
                  value={date}
                  format="DD/MM/YYYY"
                  onChange={handleDateChange}
                  slotProps={{ textField: { size: "small", fullWidth: true } }}
                />
              </Grid>
              <Grid size={HALF_WIDTH}>
                <CodeMultiSelect
                  label="สินค้า"
                  options={lookups.productOptions}
                  value={productCodes}
                  onChange={handleProductChange}
                  helperText="ไม่บังคับ · ว่าง = แสดงสินค้าที่มีแถวเจาะจง"
                />
              </Grid>
            </Grid>
            {preview && <PreviewResult preview={preview} names={lookups} />}
          </Stack>
        </DialogContent>
      </LocalizationProvider>
      <DialogActions>
        <Button onClick={onClose}>ปิด</Button>
        <Button
          variant="contained"
          loading={loading}
          disabled={!canPreview}
          onClick={handlePreview}
        >
          คำนวณ
        </Button>
      </DialogActions>
    </Dialog>
  );
}
